import type {
  PendingRegulatoryDocument,
  RegulatoryDocumentType,
} from '@/@types/Products'
import DeliverableCard from '@/components/Products/DeliverableCard'
import { REQUIRED_CREATE_PRODUCT_REGULATORY_TYPES } from '@/components/Products/regulatory-document-types'
import { Button } from '@/components/ui/button'
import { toast } from '@/hooks/use-toast'
import { convertBytesToMB } from '@/lib/select-options'
import { UploadCloudIcon } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface StepRegulatoryDocumentsProps {
  documents: PendingRegulatoryDocument[]
  onDocumentsChange: (docs: PendingRegulatoryDocument[]) => void
  handleBack: () => void
  handleNext: () => void
}

function createLocalId() {
  return `reg-doc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function createPendingDocument(
  file: File,
  type: RegulatoryDocumentType,
): PendingRegulatoryDocument {
  const normalizedName = file.name.replace(/\.[^.]+$/, '').trim()

  return {
    localId: createLocalId(),
    file,
    name: normalizedName || file.name,
    type,
    notes: '',
  }
}

type RequiredRegulatoryDocumentType =
  (typeof REQUIRED_CREATE_PRODUCT_REGULATORY_TYPES)[number]

export default function StepRegulatoryDocuments({
  documents,
  onDocumentsChange,
  handleBack,
  handleNext,
}: StepRegulatoryDocumentsProps) {
  const { t } = useTranslation('common')
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const slots: Array<{
    index: number
    type: RequiredRegulatoryDocumentType | 'other'
    title: string
    description: string
    required: boolean
    allowMultiple?: boolean
  }> = [
    {
      index: 1,
      type: 'contract',
      title: t('regulatoryDocumentsSlotContractTitle', {
        defaultValue: 'Contrato com fabricante',
      }),
      description: t('regulatoryDocumentsSlotContractDescription', {
        defaultValue:
          'Envie o contrato assinado com o fabricante, fulfillment ou responsável pela fabricação e entrega do produto.',
      }),
      required: true,
    },
    {
      index: 2,
      type: 'coa',
      title: t('regulatoryDocumentsSlotCoaTitle', {
        defaultValue: 'COA',
      }),
      description: t('regulatoryDocumentsSlotCoaDescription', {
        defaultValue: 'Certificado de análise do produto.',
      }),
      required: true,
    },
    {
      index: 3,
      type: 'label',
      title: t('regulatoryDocumentsSlotLabelTitle', {
        defaultValue: 'Rótulo do produto com ingredientes',
      }),
      description: t('regulatoryDocumentsSlotLabelDescription', {
        defaultValue:
          'Envie a imagem do rótulo com a composição e ingredientes.',
      }),
      required: true,
    },
    {
      index: 4,
      type: 'other',
      title: t('regulatoryDocumentsSlotOtherTitle', {
        defaultValue: 'Documentos extras',
      }),
      description: t('regulatoryDocumentsSlotOtherDescription', {
        defaultValue: 'Você pode enviar 1 ou mais documentos complementares.',
      }),
      required: false,
      allowMultiple: true,
    },
  ]

  const acceptedFilesDescription = t('regulatoryDocumentsAcceptedFiles', {
    defaultValue: 'PNG, JPG, JPEG ou PDF até 50 MB',
  })
  const uploadLabel = t('regulatoryDocumentsUploadLabel', {
    defaultValue: 'Clique para carregar',
  })
  const replaceFileLabel = t('regulatoryDocumentsReplaceFile', {
    defaultValue: 'Trocar arquivo',
  })
  const invalidFileTypeMessage = t('regulatoryDocumentsInvalidFileType', {
    defaultValue: 'Envie um arquivo PNG, JPG, JPEG ou PDF.',
  })

  const requiredDocumentTypeSet = new Set<string>(
    REQUIRED_CREATE_PRODUCT_REGULATORY_TYPES,
  )
  const extraDocuments = documents.filter(
    (document) => !requiredDocumentTypeSet.has(document.type || 'other'),
  )

  const isAcceptedFile = useCallback((file: File) => {
    const acceptedMimeTypes = ['application/pdf', 'image/png', 'image/jpeg']
    const acceptedExtensions = ['.pdf', '.png', '.jpg', '.jpeg']
    const lowerCaseName = file.name.toLowerCase()

    return (
      acceptedMimeTypes.includes(file.type) ||
      acceptedExtensions.some((extension) => lowerCaseName.endsWith(extension))
    )
  }, [])

  const getValidatedFiles = useCallback(
    (files: File[]) => {
      return files.filter((file) => {
        if (file.size > 50 * 1024 * 1024) {
          toast({ variant: 'error', description: t('file_max_50MB') })
          return false
        }

        if (!isAcceptedFile(file)) {
          toast({
            variant: 'error',
            description: invalidFileTypeMessage,
          })
          return false
        }

        return true
      })
    },
    [invalidFileTypeMessage, isAcceptedFile, t],
  )

  const getDocumentByType = useCallback(
    (type: RequiredRegulatoryDocumentType) =>
      documents.find((document) => document.type === type),
    [documents],
  )

  const handleFileChange = useCallback(
    (type: RequiredRegulatoryDocumentType) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextFile = getValidatedFiles(Array.from(e.target.files || []))[0]
      e.target.value = ''

      if (!nextFile) return

      onDocumentsChange([
        ...documents.filter((document) => document.type !== type),
        createPendingDocument(nextFile, type),
      ])
    },
    [documents, getValidatedFiles, onDocumentsChange],
  )

  const handleExtraFilesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextFiles = getValidatedFiles(Array.from(e.target.files || []))
      e.target.value = ''

      if (nextFiles.length === 0) return

      onDocumentsChange([
        ...documents,
        ...nextFiles.map((file) => createPendingDocument(file, 'other')),
      ])
    },
    [documents, getValidatedFiles, onDocumentsChange],
  )

  const handleRemoveDocument = (localId: string) => {
    onDocumentsChange(documents.filter((d) => d.localId !== localId))
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        setShowValidationErrors(true)

        const hasMissingRequiredDocuments =
          REQUIRED_CREATE_PRODUCT_REGULATORY_TYPES.some(
            (type) => !documents.some((document) => document.type === type),
          )

        if (hasMissingRequiredDocuments) {
          return
        }

        handleNext()
      }}
      className="w-full max-w-4xl"
    >
      <h1 className="mb-2 text-center text-2xl font-bold">
        {t('regulatoryDocumentsCreateTitle', {
          defaultValue: 'Faça o upload dos documentos complementares',
        })}
      </h1>
      <p className="mb-8 text-center text-sm text-muted">
        {t('regulatoryDocumentsCreateSubtitle', {
          defaultValue:
            'Envie os arquivos necessários para análise do produto. Os 3 primeiros itens são obrigatórios.',
        })}
      </p>

      <section className="space-y-4">
        {slots.map((slot) => {
          const document =
            slot.type === 'other' ? null : getDocumentByType(slot.type)
          const hasError =
            showValidationErrors && slot.required && !document
          const inputId = `create-regulatory-document-${slot.type}`

          return (
            <div
              key={slot.type}
              className={`rounded-2xl border w-full p-5 transition-colors ${
                hasError
                  ? 'border-red-500 bg-red-500/5'
                  : 'border-white/10 bg-[#09090b]'
              }`}
            >
              <div className="flex flex-col w-full gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="w-full">
                  <div className="mb-3 flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1D2A10] text-sm font-semibold text-[#B6E53E]">
                      {slot.index}
                    </span>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold text-white">
                        {slot.title}
                      </h2>                     
                    </div>
                  </div>

                  <p className="text-sm leading-6 text-muted">
                    {slot.description}
                  </p>
                </div>

                <div className="w-full">
                  {slot.type === 'other' ? (
                    <div className="space-y-3">
                      <label
                        htmlFor={inputId}
                        className="flex min-h-[132px] cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-white/15 bg-white/[0.02] px-6 py-5 text-center transition-colors hover:border-white/30"
                        data-cy={`regulatory-document-input-${slot.type}`}
                      >
                        <UploadCloudIcon
                          width={28}
                          height={28}
                          className="text-[#52525B]"
                        />
                        <span className="mt-4 text-sm font-medium text-white">
                          {uploadLabel}
                        </span>
                        <span className="mt-1 text-xs text-muted">
                          {acceptedFilesDescription}
                        </span>
                        <input
                          id={inputId}
                          type="file"
                          multiple={slot.allowMultiple}
                          accept=".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf"
                          className="hidden"
                          onChange={handleExtraFilesChange}
                        />
                      </label>

                      {extraDocuments.map((extraDocument) => (
                        <DeliverableCard
                          key={extraDocument.localId}
                          id="create"
                          deliverableName={extraDocument.file.name}
                          deliverableSize={convertBytesToMB(extraDocument.file.size)}
                          onDelete={() => handleRemoveDocument(extraDocument.localId)}
                        />
                      ))}
                    </div>
                  ) : document ? (
                    <div className="space-y-3 w-full">
                      <DeliverableCard
                        id="create"
                        deliverableName={document.file.name}
                        deliverableSize={convertBytesToMB(document.file.size)}
                        onDelete={() => handleRemoveDocument(document.localId)}
                      />

                      <label
                        htmlFor={inputId}
                        className="inline-flex cursor-pointer text-sm text-interactive hover:underline"
                      >
                        {replaceFileLabel}
                        <input
                          id={inputId}
                          type="file"
                          accept=".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf"
                          className="hidden"
                          onChange={handleFileChange(slot.type)}
                        />
                      </label>
                    </div>
                  ) : (
                    <label
                      htmlFor={inputId}
                      className={`flex min-h-[132px] cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed px-6 py-5 text-center transition-colors ${
                        hasError
                          ? 'border-red-500 bg-red-500/5'
                          : 'border-white/15 bg-white/[0.02] hover:border-white/30'
                      }`}
                      data-cy={`regulatory-document-input-${slot.type}`}
                    >
                      <UploadCloudIcon
                        width={28}
                        height={28}
                        className="text-[#52525B]"
                      />
                      <span className="mt-4 text-sm font-medium text-white">
                        {uploadLabel}
                      </span>
                      <span className="mt-1 text-xs text-muted">
                        {acceptedFilesDescription}
                      </span>
                      <input
                        id={inputId}
                        type="file"
                        accept=".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf"
                        className="hidden"
                        onChange={handleFileChange(slot.type)}
                      />
                    </label>
                  )}

                  {hasError ? (
                    <p className="mt-2 text-sm text-red-500">
                      {t('required_field')}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          )
        })}
      </section>

      <div className="flex justify-center gap-6 mt-8">
        <Button type="button" onClick={handleBack} variant="ghost">
          {t('back')}
        </Button>
        <Button
          type="submit"
          variant="default"
          data-cy="regulatory-docs-next"
        >
          {t('next')}
        </Button>
      </div>
    </form>
  )
}
